import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useUIContext } from 'lib/context/UIContext/UIContext';
import { cn } from 'lib/helpers/utility';

import Breadcrumb from 'components/shared/Breadcrumb/Breadcrumb';
import { useBreadcrumbContext } from 'components/shared/Breadcrumb/context/BreadcrumbContext';
import Modal from 'components/shared/Modal/Modal';

import ModalFooter from './components/ModalFooter';
import ModalHeader from './components/ModalHeader';
import TabsTableContent from './components/TabsTableContent';

const TabsTableModalInner = ({
  tabs,
  prefix,
  initialStep = 0,
  initialValues = {},
  onSubmit = () => {},
  disableClick,
  isLoading,
  handleClose,
  headerActions,
  headerClassName,
  bodyClassName,
  insideBodyClassName,
  trimSteps,
  ...rest
}) => {
  const { current: tab, goNext, setSteps, setStep } = useBreadcrumbContext();
  const [contentValues, setContentValues] = useState({});

  const { device } = useUIContext();
  const isDesktop = device === 'desktop' || device === 'laptop';

  const [selected, setSelected] = useState({});
  const [details, setDetails] = useState(null);
  const [modal, setModal] = useState(null);
  const [modalValue, setModalValue] = useState(initialValues);
  const [skippedTabs, setSkippedTabs] = useState([]);
  const [fetchedData, setFetchedData] = useState(null);

  const resetSelections = () => {
    setSelected((old) => ({ ...old, [tab.name]: [] }));
  };

  const handleCloseWithReset = () => {
    try {
      setSelected({});
      setDetails(null);
      setModal(null);
      setModalValue({});
      setSkippedTabs([]);
      setSteps(tabs);
      setStep(initialStep);
      setContentValues({});
    } finally {
      handleClose?.();
    }
  };

  const selectedData = useMemo(() => {
    return Object.keys(selected).reduce((acc, tabName) => {
      acc[tabName] = selected[tabName]?.map((row) => row.data) || [];
      return acc;
    }, {});
  }, [selected]);

  const alwaysShowDetails = tab?.details?.alwaysShow && isDesktop;
  const showDetails = details || alwaysShowDetails;

  useEffect(() => {
    if (tab?.skipStep && !skippedTabs.includes(tab?.name)) {
      goNext();
    }
    if (!skippedTabs.includes(tab?.name)) {
      setSkippedTabs((prev) => [...prev, tab?.name]);
    }
  }, [tab]);

  const setContentValue = useCallback(
    (key, value) => setContentValues((prev) => ({ ...prev, [key]: value })),
    []
  );

  const lockNext = tab?.disableContinue
    ? typeof tab?.disableContinue === 'function'
      ? tab?.disableContinue(selected, contentValues)
      : tab?.disableContinue
    : false;

  return (
    <Modal
      width={isDesktop && showDetails ? 'full' : 'medium'}
      handleClose={handleCloseWithReset}
      bodyClassName={cn('p-0', bodyClassName)}
      shouldCloseOnEsc={!modal}
      footer={
        !isLoading && (
          <ModalFooter
            resetSelections={resetSelections}
            tab={tab}
            setDetails={setDetails}
            selectedData={selectedData}
            lockNext={lockNext}
            setModal={setModal}
            initialStep={initialStep}
            handleClose={handleCloseWithReset}
            contentValues={contentValues}
          />
        )
      }
      headerClassName={cn(
        'bg-white border-b border-neutral-200 border-solid border-t-0 border-x-0 px-4 py-3',
        headerClassName
      )}
      customHeader={
        <ModalHeader
          setDetails={setDetails}
          selectedData={selectedData}
          tab={tab}
          lockNext={lockNext}
          disableClick={disableClick}
          headerActions={headerActions}
          trimSteps={trimSteps}
        />
      }
      {...rest}>
      <TabsTableContent
        tab={tab}
        isLoading={isLoading}
        modal={modal}
        setModal={setModal}
        modalValue={modalValue}
        setModalValue={setModalValue}
        selectedData={selectedData}
        onSubmit={onSubmit}
        alwaysShowDetails={alwaysShowDetails}
        showDetails={showDetails}
        details={details}
        setDetails={setDetails}
        prefix={prefix}
        setSelected={setSelected}
        setFetchedData={setFetchedData}
        selected={selected}
        fetchedData={fetchedData}
        insideBodyClassName={insideBodyClassName}
        contentValues={contentValues}
        setContentValue={setContentValue}
      />
    </Modal>
  );
};

const TabsTableModal = ({ tabs, initialStep = 0, ...rest }) => (
  <Breadcrumb steps={tabs} initialStep={initialStep}>
    <TabsTableModalInner tabs={tabs} initialStep={initialStep} {...rest} />
  </Breadcrumb>
);

export default TabsTableModal;
